/**
 * 用途：TikTok 广告计划编辑页，支持对已有广告系列 / 广告组 / 广告的字段进行编辑
 * 路由：/campaigns/edit/tiktok/:entity/:id （entity 取值 campaign | adgroup | ad）
 */
import { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { CreateTikTokAdWizard } from './CreateTikTokAdWizard';
import type { WizardStep } from './adWizardShared';

// ─── Toast type ──────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'warning' | 'info';

// ─── Sidebar: TikTok Guide Panel ─────────────────────────────────────────────

function TikTokGuidePanel({
  step,
  objective,
  adFormat,
}: {
  step: WizardStep;
  objective: string;
  adFormat: string;
}) {
  const stepHints: Record<WizardStep, string[]> = {
    1: [
      'TikTok 广告系列先确定营销目标、预算模式和初始状态。',
      '转化/商品销售目标建议提前准备 Pixel 与优化事件，避免后续无法起量。',
      '预算尽量保持稳定，减少频繁大幅改动对学习期的干扰。',
    ],
    2: [
      '广告组负责排期、版位、出价和受众，决定系统如何拿量。',
      '自动版位更适合冷启动，手动版位会缩小流量范围。',
      '总预算或明确活动周期时，建议设置开始/结束时间。',
    ],
    3: [
      'TikTok 创意优先考虑原生感，不要直接照搬 Facebook 的横版素材。',
      '前几秒需要快速给出卖点、价格或使用场景，避免开场过慢。',
      '文案与 CTA 要和落地页保持一致，减少点击后的预期落差。',
    ],
  };

  const objectiveHints: Record<string, string[]> = {
    TRAFFIC: ['更适合用清晰利益点和明确 CTA 拉动点击。'],
    CONVERSIONS: ['需要确保 Pixel 与优化事件可用，系统才会按转化信号优化。'],
    PRODUCT_SALES: ['商品销售建议同步准备商品卖点、价格锚点和落地页转化链路。'],
    VIDEO_VIEWS: ['更适合强调前 3 秒 hook、完播节奏和画面停留。'],
    LEAD_GENERATION: ['线索目标建议在首屏明确表单收益，减少无效提交。'],
    APP_PROMOTION: ['应用推广建议突出核心功能演示和安装后的直接价值。'],
    REACH: ['覆盖目标更看重触达稳定性，素材需快速表达品牌记忆点。'],
  };

  const formatHints: Record<string, string[]> = {
    SINGLE_VIDEO: [
      '单视频建议优先竖版 9:16，画面主体放在安全区内。',
      '建议启用配音、字幕或音乐，让内容更接近 TikTok 原生体验。',
    ],
    SINGLE_IMAGE: [
      '单图片素材建议主体突出、信息层级简单，避免文字过密。',
      '图片广告也要保留明显的产品利益点和动作指令。',
    ],
    SPARK_ADS: [
      'Spark Ads 需要使用已授权的 TikTok 内容，内容 ID 必须有效。',
      '优先选择已有互动基础的帖子，更容易承接社交证明。',
    ],
  };

  const lines = [
    ...(stepHints[step] || []),
    ...(objectiveHints[objective] || []),
    ...(step === 3 ? formatHints[adFormat] || [] : []),
  ];

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface2)', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        TikTok 投放说明
      </div>
      <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: 'var(--text2)', lineHeight: 1.85 }}>
        {lines.map((line, index) => <li key={`${step}-${objective}-${adFormat}-${index}`}>{line}</li>)}
      </ul>
    </div>
  );
}

// ─── Sidebar: TikTok Creative Spec Reference ─────────────────────────────────

function TikTokCreativeSpecRef({ adFormat }: { adFormat: string }) {
  const rowsByFormat: Record<string, [string, string][]> = {
    SINGLE_VIDEO: [
      ['推荐方向', '9:16 竖版优先'],
      ['清晰度', '建议 720P 及以上'],
      ['节奏', '前 3 秒给卖点'],
      ['音频', '建议开启配音/音乐'],
      ['字幕', '避免压边，保留安全区'],
    ],
    SINGLE_IMAGE: [
      ['推荐比例', '9:16 或 1:1'],
      ['画面重点', '主体清晰、背景简洁'],
      ['文字密度', '避免整屏堆字'],
      ['卖点表达', '一屏只讲一个核心利益点'],
      ['CTA', '按钮与文案统一'],
    ],
    SPARK_ADS: [
      ['素材来源', '已发布并授权的 TikTok 内容'],
      ['必填信息', '有效的 TikTok 内容 ID'],
      ['优先选择', '已有互动基础的原生帖子'],
      ['落地方式', '可延续原贴互动与社交证明'],
      ['注意事项', '确保授权关系和身份一致'],
    ],
  };

  const rows = rowsByFormat[adFormat] || rowsByFormat.SINGLE_VIDEO;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface2)', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        TikTok 创意要点
      </div>
      <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse', lineHeight: 1.9 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: 'var(--text3)', paddingRight: 6, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
              <td style={{ color: 'var(--text2)' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helper ──────────────────────────────────────────────────────────────────

function isPublishedStatus(v: string | null): boolean {
  return (v || '').toLowerCase() === 'published';
}

const ENTITY_LABELS: Record<string, string> = {
  campaign: '广告系列',
  adset: '广告组',
  ad: '广告',
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function TikTokPlanEditPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { brandId, siteId } = useAppStore();
  const { entity = 'campaign', id = '' } = useParams<{ entity: string; id: string }>();
  const [search] = useSearchParams();

  const campaignPublished = isPublishedStatus(search.get('campaign_status'));
  const adsetPublished = isPublishedStatus(search.get('adset_status'));
  const adPublished = isPublishedStatus(search.get('ad_status'));
  const selfPublished = entity === 'campaign' ? campaignPublished : entity === 'adset' ? adsetPublished : adPublished;

  // Sidebar state — updated via wizard callbacks
  const [tikTokStep, setTikTokStep] = useState<WizardStep>(1);
  const [tikTokObjective, setTikTokObjective] = useState('TRAFFIC');
  const [tikTokAdFormat, setTikTokAdFormat] = useState('SINGLE_VIDEO');

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: ToastType } | null>(null);
  const showToast = useCallback((msg: string, type: ToastType = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500);
  }, []);

  if (!brandId || !entity || !id) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>参数缺失</div>;
  }

  return (
    <div className="page-section visible">
      {/* Toolbar */}
      <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div className="breadcrumb">
          <span style={{ cursor: 'pointer', color: 'var(--primary-light)' }} onClick={() => navigate('/campaigns/tiktok')}>广告计划</span>
          <span className="bc-sep">/</span>
          <span style={{ color: 'var(--text2)' }}>编辑 TikTok {ENTITY_LABELS[entity] || '广告'}</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/campaigns/tiktok')}>← 返回</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 20, alignItems: 'start' }}>

        {/* Left: Form Card */}
        <div className="card" style={{ padding: 24 }}>
          {/* Card Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                编辑 TikTok {ENTITY_LABELS[entity] || '广告'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text3)', padding: '1px 6px', background: 'var(--surface3)', borderRadius: 4 }}>
                #{id}
              </span>
            </div>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 4, flexShrink: 0, fontWeight: 500,
              background: selfPublished ? 'rgba(34,197,94,.1)' : 'var(--surface3)',
              color: selfPublished ? 'var(--green)' : 'var(--text3)',
            }}>
              {selfPublished ? '已发布' : '未发布'}
            </span>
          </div>

          <CreateTikTokAdWizard
            brandId={brandId}
            siteId={siteId}
            onCancel={() => navigate('/campaigns/tiktok')}
            onStepChange={setTikTokStep}
            onObjectiveChange={setTikTokObjective}
            onAdFormatChange={setTikTokAdFormat}
            onCreated={() => {
              showToast('TikTok 广告修改已保存', 'success');
              setTimeout(() => navigate('/campaigns/tiktok'), 900);
            }}
            editMode={{
              entity: entity as 'campaign' | 'adset' | 'ad',
              id: Number(id),
            }}
          />
        </div>

        {/* Right: Sticky Sidebar */}
        <div style={{ position: isMobile ? undefined : 'sticky', top: 72, maxHeight: isMobile ? undefined : 'calc(100vh - 88px)', overflowY: isMobile ? undefined : 'auto' }}>
          <TikTokGuidePanel step={tikTokStep} objective={tikTokObjective} adFormat={tikTokAdFormat} />
          <TikTokCreativeSpecRef adFormat={tikTokAdFormat} />
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, pointerEvents: 'none' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: toast.type === 'success' ? 'rgba(34,197,94,.06)' : toast.type === 'error' ? 'rgba(239,68,68,.06)' : toast.type === 'warning' ? 'rgba(251,191,36,.06)' : 'var(--surface2)',
            border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,.35)' : toast.type === 'error' ? 'rgba(239,68,68,.35)' : toast.type === 'warning' ? 'rgba(251,191,36,.35)' : 'var(--border2)'}`,
            color: toast.type === 'success' ? 'var(--green)' : toast.type === 'error' ? 'var(--red)' : toast.type === 'warning' ? 'var(--amber)' : 'var(--text)',
            borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 500,
            boxShadow: '0 8px 32px rgba(0,0,0,.45)', minWidth: 260, maxWidth: 460,
          }}>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
